<template>
    <div v-if="showTooltip" class="fight-tooltip" :class="{ 'is-visible': forceVisible }">
        <FightTimeOnTooltip :match="match" />

        <template v-if="resultType === 'bye'">
            <div>{{ translate("fight.result.details.win_by") }} {{ translate("fight.result.details.finish_type.bye") }}</div>
        </template>
        <!-- Every remaining branch reads match.result; resultType is only set for these when it is present. -->
        <template v-else-if="match.result">
            <template v-if="resultType === 'bjj2' || resultType === 'bjj3' || resultType === 'newaza'">
                <div>{{ translate("fight.result.details.win_by") }} {{ getTranslatedFightFinishType() }}</div>
                <div v-if="hasSubmissionDetail">
                    {{ translate("fight.result.details.submission") }}
                    {{ getTranslatedFightFinishSubmission() }}
                </div>
                <div>{{ translate("fight.result.details.points") }} {{ bjj2points1() }} - {{ bjj2points2() }}</div>
            </template>
            <template v-if="resultType === 'submissionFighting' || resultType === 'combatJuJutsu'">
                <div>{{ translate("fight.result.details.win_by") }} {{ getTranslatedFightFinishType() }}</div>
                <div v-if="hasSubmissionDetail">
                    {{ translate("fight.result.details.submission") }}
                    {{ getTranslatedFightFinishSubmission() }}
                </div>
                <div>
                    {{ translate("fight.result.details.points") }} {{ submissionFightingPoints1() }} -
                    {{ submissionFightingPoints2() }}
                </div>
            </template>
            <template v-if="resultType === 'mmaSimplified'">
                <div>{{ translate("fight.result.details.win_by") }} {{ getTranslatedFightFinishType() }}</div>
                <div v-if="hasSubmissionDetail">
                    {{ translate("fight.result.details.submission") }}
                    {{ getTranslatedFightFinishSubmission() }}
                </div>
            </template>
            <template v-if="resultType === 'submissionOnly' || resultType === 'submissionOnlyWithPenalties'">
                <div>{{ translate("fight.result.details.win_by") }} {{ getTranslatedFightFinishType() }}</div>
                <div v-if="hasSubmissionDetail">
                    {{ translate("fight.result.details.submission") }}
                    {{ getTranslatedFightFinishSubmission() }}
                </div>
            </template>
            <template v-if="resultType === 'wrestling' || resultType === 'grappling'">
                <div v-if="hasSubmissionDetail">
                    {{ translate("fight.result.details.finish_type.submission") }}:
                    {{ getTranslatedFightFinishSubmission() }}
                </div>
                <div v-else>{{ translate("fight.result.details.win_by") }} {{ getTranslatedFightFinishType() }}</div>
                <div>
                    {{ translate("fight.result.details.major_points") }}: {{ match.result.major1 }} -
                    {{ match.result.major2 }}
                </div>
                <div>
                    {{ translate("fight.result.details.technical_points") }}: {{ match.result.technicalPoints1 }} -
                    {{ match.result.technicalPoints2 }}
                </div>
                <div>
                    {{ translate("fight.result.details.warnings") }}: {{ match.result.penalties1 }} -
                    {{ match.result.penalties2 }}
                </div>
            </template>
            <template v-if="resultType === 'judo'">
                <div>{{ translate("fight.result.details.win_by") }} {{ getTranslatedFightFinishType() }}</div>
                <div v-if="judoPoints()">{{ translate("fight.result.details.points") }} {{ judoPoints() }}</div>
            </template>
            <template v-if="resultType === 'taekwondo'">
                <div>{{ translate("fight.result.details.win_by") }} {{ getTranslatedFightFinishType() }}</div>
                <div>{{ translate("fight.result.details.points") }} {{ match.result.points1 }} - {{ match.result.points2 }}</div>
                <div>
                    {{ translate("fight.result.details.gamjeom") }} {{ match.result.gamjeom1 }} -
                    {{ match.result.gamjeom2 }}
                </div>
            </template>
            <template v-if="resultType === 'karateKumite'">
                <div>{{ translate("fight.result.details.win_by") }} {{ getTranslatedFightFinishType() }}</div>
                <div>
                    {{ translate("fight.result.details.finish_type.wazaari") }}: {{ match.result.wazari1 }} -
                    {{ match.result.wazari2 }}
                </div>
                <div v-if="match.result.chui1 || match.result.chui2">
                    {{ translate("scoreboard.karateKumite.chui") }}: {{ match.result.chui1 }} - {{ match.result.chui2 }}
                </div>
                <div v-if="match.result.genten1 || match.result.genten2">
                    {{ translate("scoreboard.karateKumite.genten") }}: {{ match.result.genten1 }} -
                    {{ match.result.genten2 }}
                </div>
                <div v-if="karateKumiteDecisionScore()">
                    {{ translate("scoreboard.karateKumite.decisionScore") }}: {{ karateKumiteDecisionScore() }}
                </div>
            </template>
            <template v-if="resultType === 'fighting'">
                <div>{{ translate("fight.result.details.win_by") }} {{ getTranslatedFightFinishType() }}</div>
                <div>{{ translate("fight.result.details.points") }} {{ match.result.totalPoints1 }} - {{ match.result.totalPoints2 }}</div>
                <div v-if="match.result.shido1 || match.result.shido2">
                    {{ translate("scoreboard.fighting.shido") }}: {{ match.result.shido1 }} - {{ match.result.shido2 }}
                </div>
                <div v-if="match.result.chui1 || match.result.chui2">
                    {{ translate("scoreboard.fighting.chui") }}: {{ match.result.chui1 }} - {{ match.result.chui2 }}
                </div>
            </template>
            <template v-if="resultType === 'doubleDisqualification'">
                <div>{{ translate("fight.result.details.finish_type.doubleDisqualification") }}</div>
            </template>
            <template v-if="resultType === 'fightCancelled'">
                <div>
                    {{ translate("fight.result.details.finish_type.fightCancelled") }}
                </div>
            </template>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import FightTimeOnTooltip from "./FightTimeOnTooltip.vue";
    import { translate } from "../Common/translations";
    import { BracketMatch, ResultType, SUPPORTED_RESULT_TYPES } from "../Common/Fights/types";

    const props = defineProps<{
        match: BracketMatch;
        forceVisible?: boolean;
    }>();

    const hasSubmissionDetail = computed(() => {
        if (props.match.result?.type === "judo") {
            return false;
        }
        return props.match && props.match.result && !!props.match.result.submission;
    });

    const resultType = computed<ResultType | null>(() => {
        if (props.match && props.match.result && props.match.result.fightFinishType === "doubleDisqualification") {
            return "doubleDisqualification";
        }
        if (isByeFightWinner()) {
            return "bye";
        }
        if (props.match && props.match.result && props.match.result.type) {
            return props.match.result.type;
        }
        return null;
    });

    const showTooltip = computed(() => {
        if (!resultType.value) {
            return false;
        }

        return SUPPORTED_RESULT_TYPES.indexOf(resultType.value) > -1;
    });

    function isByeFightWinner() {
        if (props.match.player1.id === 0 && (props.match.player2.id ?? 0) > 0 && props.match.player2.winner) {
            return true;
        }
        if (props.match.player2.id === 0 && (props.match.player1.id ?? 0) > 0 && props.match.player1.winner) {
            return true;
        }
        return false;
    }

    function getTranslatedFightFinishType() {
        if (props.match && props.match.result && props.match.result.fightFinishType) {
            const key = `fight.result.details.finish_type.${props.match.result.fightFinishType}`;
            return translate(key);
        }
        return "";
    }

    function getTranslatedFightFinishSubmission() {
        if (props.match.result?.type === "judo") {
            return "";
        }

        if (props.match && props.match.result && props.match.result.submission) {
            const key = `submission.${props.match.result.submission}`;
            return translate(key);
        }
        return "";
    }

    function bjj2points1() {
        const r = props.match.result!;
        return `${r.major1}:${r.advantages1}:${r.penalties1}`;
    }

    function bjj2points2() {
        const r = props.match.result!;
        return `${r.major2}:${r.advantages2}:${r.penalties2}`;
    }

    function submissionFightingPoints1() {
        const r = props.match.result!;
        return `${r.major1}:${r.penalties1}`;
    }

    function submissionFightingPoints2() {
        const r = props.match.result!;
        return `${r.major2}:${r.penalties2}`;
    }

    function karateKumiteDecisionScore(): string | null {
        const ds = props.match.result!.decisionScore;
        if (!ds || ds === "notRecorded") {
            return null;
        }
        const decisionScoreTranslations = {
            unanimous: "fight.result.details.finish_type.judgesDecisionUnanimous",
            split: "fight.result.details.finish_type.judgesDecisionSplit",
            overtime: "scoreboard.karateKumite.overtimeDecision",
        };
        return translate(decisionScoreTranslations[ds]);
    }

    function judoPoints(): string | null {
        if (props.match.result?.type !== "judo") {
            return null;
        }

        if (props.match.result.fightFinishType === "ippon") {
            if (props.match.player1.winner) {
                return `100 - 0`;
            } else {
                return `0 - 100`;
            }
        }

        if (props.match.result.fightFinishType === "wazaari") {
            if (props.match.player1.winner) {
                return `10 - 0`;
            } else {
                return `0 - 10`;
            }
        }
        if (props.match.result.fightFinishType === "yuko") {
            if (props.match.player1.winner) {
                return `1 - 0`;
            } else {
                return `0 - 1`;
            }
        }

        return null;
    }
</script>
